import { useState, useEffect, useMemo } from 'react';

import { Card, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { subMonths } from 'date-fns';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Bar,
  BarChart,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';

import { fetchSummaryStats, fetchTemporalStats } from 'api';
import { colors } from 'config/theme';
import { getDelta, getErrorDetail } from 'helpers';
import { useSnackContext } from 'hooks/useSnackContext';
import { SummaryStats, TemporalStats } from 'types';

import { BarometerCard } from '../BarometerCard';
import { pieColors } from './data';
import { convertValueToLabel } from './helper';
import { PieChartLegend } from './PieChartLegend/PieChartLegend';
export const Overview = () => {
  const { setSnack } = useSnackContext();
  const [summaryStats, setSummaryStats] = useState<SummaryStats | null>(null);
  const [statsDelta, setStatsDelta] = useState<TemporalStats | null>(null);

  useEffect(() => {
    const getSummaryStats = async () => {
      try {
        const { data } = await fetchSummaryStats();
        setSummaryStats(data);
      } catch (e: any) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: getErrorDetail({
            error: e,
            fallback: 'Failed To Fetch Stats',
          }),
          error: e,
        }));
      }
    };
    getSummaryStats();

    return () => {
      setSummaryStats(null);
    };
  }, [setSnack]);

  useEffect(() => {
    const getDelta = async () => {
      try {
        const todaysDate = new Date();
        const { data } = await fetchTemporalStats({
          start_date: subMonths(todaysDate, 1),
          end_date: todaysDate,
        });
        setStatsDelta(data);
      } catch (e: any) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: getErrorDetail({
            error: e,
            fallback: 'Failed To Fetch Delta Stats',
          }),
          error: e,
        }));
      }
    };
    getDelta();

    return () => {
      setStatsDelta(null);
    };
  }, [setSnack]);

  const stripKey = (key: string) => {
    return key
      .replace('candidates_', '')
      .split('_')
      .map((string) => string.toUpperCase())
      .join(' ');
  };
  const pieChartData = useMemo(() => {
    if (!summaryStats) {
      return [];
    }

    const summaryStatsArray = Object.entries(summaryStats)
      .map((entry) => entry)
      .filter((entry) =>
        [
          'candidates_awaiting_assignment',
          'candidates_submitted',
          'candidates_pending_signature',
          'candidates_under_review',
        ].includes(entry[0]),
      )
      .map((entry) => {
        const keyString = stripKey(entry[0]);
        return { name: keyString, value: entry[1] };
      });
    return summaryStatsArray;
  }, [summaryStats]);
  const flagBarChartData = useMemo(() => {
    return summaryStats?.flag_stats?.flag_data_by_type
      ? summaryStats.flag_stats.flag_data_by_type.map((flag) => ({
          label: convertValueToLabel(flag.flag_name),
          count: parseInt(flag.count, 10),
        }))
      : [];
  }, [summaryStats]);
  return (
    <Grid container spacing={3}>
      <Grid item xs={4} display='flex' direction='column'>
        <Typography sx={{ color: colors.blue }} variant='h6'>
          Employers Onboarded
        </Typography>
        <Typography variant='body2'>
          Total number of employers enrolled in Rabbit with at least one user.
        </Typography>
        <BarometerCard
          sx={{ mt: 'auto' }}
          barometer={summaryStats?.enrolled_employers}
          directTo='/home/candidate_admin_dashboard/organizations?assigned=assigned&type=top&is_employer=true'
          arialabel='Total number of employers enrolled in Rabbit with at least one user.'
          indicator={{
            label: 'Monthly Increase',
            value: getDelta({
              magnitude: summaryStats?.total_employers,
              change: statsDelta?.newly_reviewable_org_count,
            }),
          }}
        />
      </Grid>
      <Grid item xs={4} display='flex' direction='column'>
        <Typography sx={{ color: colors.blue }} variant='h6' component='h1'>
          Associated Entities Onboarded
        </Typography>
        <Typography variant='body2'>
          Total number of employers associated with top-level organizations.
        </Typography>
        <BarometerCard
          sx={{ mt: 'auto' }}
          barometer={summaryStats?.associated_enrolled_employers}
          directTo='/home/candidate_admin_dashboard/organizations?assigned=assigned&type=associated'
          arialabel='Total number of employers associated with top-level organizations.'
        />
      </Grid>
      <Grid item xs={4} display='flex' direction='column'>
        <Typography
          sx={{ color: colors.blue }}
          color='textSecondary'
          variant='h6'
          component='h1'
        >
          Employers with Candidates
        </Typography>
        <Typography variant='body2'>
          Total number of parent organizations with at least one candidate in
          Rabbit.
        </Typography>
        <BarometerCard
          sx={{ mt: 'auto' }}
          barometer={summaryStats?.total_employers}
          directTo='/home/candidate_admin_dashboard/organizations?type=top'
          arialabel='Total number of parent organizations with at least one candidate in
          Rabbit'
          indicator={{
            label: 'Newly Reviewable',
            value: statsDelta?.newly_reviewable_org_count.toString() ?? '--',
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography sx={{ color: colors.blue }} variant='h6' component='h1'>
          Candidate Status
        </Typography>
        {pieChartData.length > 0 ? (
          <Card>
            <PieChart
              width={700}
              height={400}
              margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
            >
              <Pie
                data={pieChartData}
                dataKey='value'
                nameKey='name'
                innerRadius={60}
                outerRadius={80}
                fill='#8884d8'
                cx='25%'
              >
                {pieChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}`}
                    fill={pieColors[index % pieColors.length]}
                  />
                ))}
              </Pie>
              <Legend
                width={350}
                align='right'
                verticalAlign='middle'
                content={<PieChartLegend />}
              />
            </PieChart>
          </Card>
        ) : (
          <Skeleton variant='rectangular' width='100%' height={400} />
        )}
      </Grid>
      <Grid item xs={6}>
        <Typography
          sx={{ color: colors.blue }}
          color='textSecondary'
          variant='h6'
          component='h1'
        >
          Tranche Eligible Candidates
        </Typography>
        <Typography variant='body2'>
          Total number of candidates eligible to be added to a tranche.
        </Typography>
        <BarometerCard
          barometer={summaryStats?.candidates_tranche_eligible}
          directTo='/home/candidate_admin_dashboard/reviewed_candidates?review_status_includes=reviewed_and_verified&tranche_id=0'
          arialabel='Total number of candidates eligible to be added to a tranche.'
          indicator={{
            label: 'Tranched This Month',
            value: statsDelta?.tranched_candidate_count.toString() ?? '--',
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Typography
          sx={{ color: colors.blue }}
          color='textSecondary'
          variant='h6'
          component='h1'
        >
          Total Candidates with Flags
        </Typography>
        <Typography variant='body2'>
          Total number of candidates with at least one flag.
        </Typography>
        <BarometerCard
          barometer={summaryStats?.flag_stats?.total_flagged_candidates}
          arialabel='Total number of candidates with at least one flag.'
          directTo='/home/candidate_admin_dashboard/reviewed_candidates?flag_types_include=contract_date_mismatch&flag_types_include=work_period_quantity&flag_types_include=employer_not_in_rabbit&flag_types_include=dod_complete&flag_types_include=derog_flag&flag_types_include=admin_flag&flag_types_include=has_non_dod_work_periods&flag_types_include=state_action_required&flag_types_include=unable_to_verify'
        />
      </Grid>

      <Grid item xs={12}>
        <Typography
          sx={{ color: colors.blue }}
          color='textSecondary'
          variant='h6'
          component='h1'
        >
          Candidates with Flags
        </Typography>
        <Typography variant='body2'>
          Breakdown of candidates by flag type.
        </Typography>
        {flagBarChartData.length ? (
          <Paper sx={{ padding: 2 }}>
            <ResponsiveContainer width='100%' height={350}>
              <BarChart
                // width={700}
                // height={400}
                data={flagBarChartData}
                layout='vertical'
                barCategoryGap={15}
                margin={{ top: 0, right: 0, left: 50, bottom: 0 }}
              >
                <YAxis
                  type='category'
                  axisLine={false}
                  dataKey='label'
                  tickLine={false}
                  width={100}
                  style={{
                    fontSize: '.7rem',
                  }}
                />
                <XAxis type='number' axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey='count' fill={colors.uswds.primary.vivid} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        ) : (
          <Skeleton variant='rectangular' width='100%' height={350} />
        )}
      </Grid>
      <Grid item xs={4} display='flex' direction='column'>
        <Typography sx={{ color: colors.blue }} variant='h6' component='h1'>
          U.S. Companies
        </Typography>
        <Typography variant='body2'>
          Top-level Organizations onboarded. This number may include
          subsidiaries, including in other countries.
        </Typography>
        <BarometerCard
          sx={{ mt: 'auto' }}
          barometer={summaryStats?.country_stats?.total_us_organizations}
          arialabel='Top-level Organizations onboarded. This number may include
          subsidiaries, including in other countries.'
          directTo='/home/candidate_admin_dashboard/organizations?country=US'
          indicator={{
            label: 'Associated Organizations',
            value:
              summaryStats?.country_stats?.total_us_associated_organizations.toString() ??
              '--',
          }}
        />
      </Grid>
      <Grid item xs={4} display='flex' direction='column'>
        <Typography sx={{ color: colors.blue }} variant='h6' component='h1'>
          Afghan Companies
        </Typography>
        <Typography variant='body2'>
          Top-level Organizations onboarded. This number may include
          subsidiaries, including in other countries.
        </Typography>
        <BarometerCard
          sx={{ mt: 'auto' }}
          barometer={summaryStats?.country_stats?.total_afg_organizations}
          arialabel='Top-level Organizations onboarded. This number may include
          subsidiaries, including in other countries.'
          directTo='/home/candidate_admin_dashboard/organizations?country=AF'
          indicator={{
            label: 'Associated Organizations',
            value:
              summaryStats?.country_stats?.total_afg_associated_organizations.toString() ??
              '--',
          }}
        />
      </Grid>
      <Grid item xs={4} display='flex' direction='column'>
        <Typography sx={{ color: colors.blue }} variant='h6' component='h1'>
          European / Other Companies
        </Typography>
        <Typography variant='body2'>
          Top-level Organizations onboarded. This number may include
          subsidiaries, including in other countries.
        </Typography>
        <BarometerCard
          sx={{ mt: 'auto' }}
          barometer={summaryStats?.country_stats?.total_other_organizations}
          arialabel='Top-level Organizations onboarded. This number may include
          subsidiaries, including in other countries.'
          indicator={{
            label: 'Associated Organizations',
            value:
              summaryStats?.country_stats?.total_other_associated_organizations.toString() ??
              '--',
          }}
        />
      </Grid>
    </Grid>
  );
};
